'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

import { GroqImage } from '@/helpers/Image';
import type { GROQImage } from '@/sanity/lib/definitions';
import { EXPO } from './reveal';

const DRAW = [0.65, 0, 0.35, 1] as const;
const SIZES = '(min-width: 993px) 62.5vw, (min-width: 640px) 1116px, 257vw';

export default function EdgeScene({
  image,
  mic,
  sticker,
}: {
  image?: GROQImage;
  mic?: GROQImage;
  sticker?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.25 });
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!inView) return;
    if (loaded) {
      setReady(true);
      return;
    }
    const timer = window.setTimeout(() => setReady(true), 900);
    return () => window.clearTimeout(timer);
  }, [inView, loaded]);

  const on = reduce || inView;

  return (
    <div
      ref={ref}
      className="relative aspect-[1.0076/1] w-full overflow-hidden rounded-[1rem] bg-[#050B09] lg:aspect-[1200/410] lg:rounded-[0.5rem]">
      <div className="absolute left-[calc(50%-69.46rem)] top-[0.65rem] aspect-[1200/658.125] w-[139.5rem] lg:left-0 lg:top-0 lg:w-full">
        {image && (
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 1.02, filter: 'blur(12px)' }}
            animate={reduce || ready ? { opacity: 1, scale: 1, filter: 'blur(0px)' } : undefined}
            transition={{
              opacity: { duration: 0.9, ease: EXPO },
              scale: { duration: 1.2, ease: EXPO },
              filter: { duration: 1, ease: EXPO },
            }}
            className="absolute inset-0">
            <GroqImage
              src={image}
              alt=""
              quality={100}
              sizes={SIZES}
              onLoad={() => setLoaded(true)}
              style={{ width: '100%' }}
              className="!h-full !w-full object-cover"
            />
          </motion.div>
        )}
        <div
          aria-hidden
          className="absolute inset-0 bg-[linear-gradient(180deg,#050B09_0%,rgba(5,11,9,0.2)_34%,rgba(5,11,9,0)_62%,#050B09_86%)]"
        />
        {mic && (
          <motion.div
            initial={reduce ? false : { y: '46%', opacity: 0 }}
            animate={on ? { y: 0, opacity: 1 } : undefined}
            transition={{ y: { duration: 1.8, ease: EXPO }, opacity: { duration: 0.7, ease: 'easeOut' } }}
            className="absolute left-[3.0833%] top-[-9.7412%] aspect-[16/9] w-full">
            <GroqImage
              src={mic}
              alt=""
              quality={100}
              sizes={SIZES}
              style={{ width: '100%' }}
              className="!h-full !w-full object-cover"
            />
          </motion.div>
        )}
      </div>
      <div
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,11,9,0)_40.683%,#050B09_85.683%)] lg:hidden"
      />

      {sticker && (
        <>
          <svg
            viewBox="0 0 135.5 27.342"
            fill="none"
            aria-hidden
            className="absolute left-[46.156rem] top-[13.563rem] hidden h-[3.4178rem] w-[16.9375rem] overflow-visible lg:block">
            <motion.path
              d="M.75 26.59C15.75 11.59 41.75 4.59 67.75 2.34 93.75.09 119.75 2.59 134.75 6.59"
              stroke="rgba(255,255,255,0.75)"
              strokeWidth={1.5}
              strokeLinecap="round"
              initial={reduce ? false : { pathLength: 0 }}
              animate={on ? { pathLength: 1 } : undefined}
              transition={{ duration: 1.1, ease: DRAW, delay: 0.35 }}
            />
            <motion.path
              d="M131.37.75 134.6 6.47 128.91 9.97"
              stroke="rgba(255,255,255,0.75)"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reduce ? false : { opacity: 0 }}
              animate={on ? { opacity: 1 } : undefined}
              transition={{ duration: 0.25, delay: 1.35 }}
            />
          </svg>
          <div className="absolute inset-x-0 top-[34.875rem] flex justify-center lg:inset-x-auto lg:left-[22.5rem] lg:top-[17.625rem] lg:block">
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 10, scale: 0.94 }}
              animate={on ? { opacity: 1, y: 0, scale: 1 } : undefined}
              transition={{ opacity: { duration: 0.5 }, default: { duration: 0.7, ease: EXPO } }}>
              <p className="rze-float m-0 whitespace-nowrap rounded-[0.5rem] bg-[#FFE11A] px-[1.5rem] py-[0.75rem] font-geist text-[2.25rem] font-[500] leading-[3rem] text-black">
                {sticker}
              </p>
            </motion.div>
          </div>
        </>
      )}

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit] border border-white/[0.1]"
      />
      <style
        jsx
        global>{`
        @media (prefers-reduced-motion: no-preference) {
          .rze-float {
            animation: rze-float 10.5s ease-in-out infinite;
          }
        }
        @keyframes rze-float {
          0%,
          100% {
            transform: translateY(-0.3125rem);
          }
          50% {
            transform: translateY(0.3125rem);
          }
        }
      `}</style>
    </div>
  );
}
